import { defaultConfig as formKitDefaultConfig, createInput } from '@formkit/vue';
import { options as formKitOptionsFeature } from '@formkit/inputs';

//import formkit custom input components
import FormulateDateSelector from './components/Formulate/FormulateDateSelector/FormulateDateSelector.vue';
import FormulateLabel from './components/Formulate/FormulateLabel/FormulateLabel.vue';
import FormulateOptionSelector from './components/Formulate/FormulateOptionSelector/FormulateOptionSelector.vue';
import FormulateStatusSelector from './components/Formulate/FormulateStatusSelector/FormulateStatusSelector.vue';
import FormulateAsyncSelect from './components/Formulate/FormulateAsyncSelect/FormulateAsyncSelect.vue';
import FormulateAddress from './components/Formulate/FormulateAddress/FormulateAddress.vue';
import FormulateBoolFilter from './components/Formulate/FormulateBoolFilter/FormulateBoolFilter.vue';
import FormulateSimpleSelect from './components/Formulate/FormulateSimpleSelect/FormulateSimpleSelect.vue';
import FormulateBoxSelect from './components/Formulate/FormulateBoxSelect/FormulateBoxSelect.vue';
import FormulateTreeSelect from './components/Formulate/FormulateTreeSelect/FormulateTreeSelect.vue';
import FormulateAvSelect from './components/Formulate/FormulateAvSelect/FormulateAvSelect.vue';
import FormulateAsyncOptionSelector from './components/Formulate/FormulateAsyncOptionSelector/FormulateAsyncOptionSelector.vue';
import FormulateBinSlider from './components/Formulate/FormulateBinSlider/FormulateBinSlider.vue';
import FormulateCheckboxCards from './components/Formulate/FormulateCheckboxCards/FormulateCheckboxCards.vue';
import FormulatePassword from './components/Formulate/FormulatePassword/FormulatePassword.vue';
import FormulateTriCheckbox from './components/Formulate/FormulateTriCheckbox/FormulateTriCheckbox.vue';
import FormulateTagField from './components/Formulate/FormulateTagField/FormulateTagField.vue';

import { checkEmail } from './utils/validationRules';
import _debounce from 'lodash/debounce';

const debouncedCheckEmail = _debounce((value, resolve) => checkEmail(value).then(resolve), 500);

function isValidEmailAddress(value) {
	return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

const legends = ['checkbox_multi', 'radio_multi', 'repeater', 'transferlist'];

function customLabelPlugin(node) {
  if (['button', 'submit', 'hidden', 'group', 'list', 'meta', 'formkitAccordion'].includes(node.props.type)) return;

  node.on('created', () => {
    const legendOrLabel = legends.includes(`${node.props.type}${node.props.options ? '_multi' : ''}`) ? 'legend' : 'label';

    if (node.props?.definition?.schemaMemoKey) {
      node.props.definition.schemaMemoKey += `${node.props.options ? '_multi' : ''}_add_asterisk`;
    }

    const schemaFn = node.props.definition.schema;

    node.props.definition.schema = (sectionsSchema = {}) => {
      sectionsSchema[legendOrLabel] = {
        children: [
          {
            $el: 'span',
            attrs: {
              innerHTML: '$label',
            },
          },
          {
            $cmp: 'InfoTooltip',
            if: '$attrs.tooltip',
            props: {
              content: '$attrs.tooltip',
            },
          },
        ],
      };

      return schemaFn(sectionsSchema);
    };
  });
}

const formKitConfig = formKitDefaultConfig({
	plugins: [
		customLabelPlugin,
	],
	inputs: {
		dateSelector: createInput(FormulateDateSelector, { props: ['startDate'] }),
		vueSelect: createInput(FormulateOptionSelector),
		optionSelect: createInput(FormulateOptionSelector),
		statusSelect: createInput(FormulateStatusSelector, { props: ['colors'] }),
		asyncSelect: createInput(FormulateAsyncSelect),
		asyncOptionSelect: createInput(FormulateAsyncOptionSelector),
		boolFilter: createInput(FormulateBoolFilter, { props: ['yesLabel', 'noLabel'] }),
		addressSelect: createInput(FormulateAddress, { props: ['bounds'] }),
		boxSelect: createInput(FormulateBoxSelect, { props: ['options', 'colors', 'icons'], features: [formKitOptionsFeature] }),
		simpleSelect: createInput(FormulateSimpleSelect),
		treeSelect: createInput(FormulateTreeSelect),
		avSelect: createInput(FormulateAvSelect, { props: ['labelFormatter'] }),
		binSlider: createInput(FormulateBinSlider, { props: ['min', 'max', 'interval'] }),
		checkboxCards: createInput(FormulateCheckboxCards, { props: ['icon', 'customLabel', 'options'], features: [formKitOptionsFeature] }),
		cPassword: createInput(FormulatePassword, { props: ['icon', 'customLabel'] }),
		triCheckbox: createInput(FormulateTriCheckbox),
		tagField: createInput(FormulateTagField, { props: ['collection', 'tagModel', 'emptyMessage'] }),
	},
	messages: {
		en: {
			ui: {
				// Hide FormKit's default English form-level banner.
				incomplete: '',
			},
			validation: {
				required() {
					return `Dit veld is vereist.`;
				},
				length({ args, node }) {
					const min = args[0];
					const max = args[1];
					const value = node && node.value;
					const len = value == null ? 0 : String(value).length;

					if (min != null && len < Number(min)) {
						return `Dit veld moet minimum ${min} karakters bevatten.`;
					}
					if (max != null && len > Number(max)) {
						return `Dit veld mag maximum ${max} karakters bevatten.`;
					}
					if (max != null) {
						return `Dit veld mag maximum ${max} karakters bevatten.`;
					}
					return `Dit veld moet minimum ${min} karakters bevatten.`;
				},
				email() {
					return `Gelieve een geldig e-mailadres in te vullen.`;
				},
				emailAvailable() {
					return `Dit e-mailadres is al in gebruik.`;
				},
			},
		},
	},
	rules: {
		emailAvailable: ({ value }) => {
			const email = typeof value === 'string' ? value.trim() : '';

			if (!isValidEmailAddress(email)) {
				return true;
			}

			return new Promise(resolve => {
				debouncedCheckEmail(email, resolve);
			});
		}
	}
});

export default formKitConfig;
